import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import ImageAnnotationTool from '../videoPage/ImageAnnotationTool';
import SideBar from '../sideBar';
import { IS_ANNOTATION } from 'store/actions';
import EvidenceVideo from './EvidenceVideo';
import 'chart.js/auto';
import ReactApexChart from 'react-apexcharts';
import RightEvidenceBar from '../videoPage/RightEvidenceBar';

const EvidencePage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const targetRef = useRef(null);
  const [source, setSource] = useState('');
  const [isRightBarVisible] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [
    isEvidenceRightBarVisible
    //  setRightBarVisible
  ] = useState(true);
  const isAnnotation = useSelector((state) => state.cameraReducer.isAnnotation);
  const checkImage = useSelector((state) => state.cameraReducer.checkImage);
  const currentDate = useSelector((state) => state.customization.currentDate);

  const selectedCamera = useSelector((state) => state.cameraReducer.cameraData);
  const videoRef = useRef(null);
  const [videoData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const selectedSource = useSelector((state) => state.customization.videoSource);

  useEffect(() => {
    if (checkImage) {
      dispatch({ type: IS_ANNOTATION, isAnnotation: true });
    }
  }, [checkImage, dispatch]);

  useEffect(() => {
    fetchCameras();
  }, []);

  const fetchCameras = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('https://db.neophyte.live/sentinel-api/api/sentinel/getEvidenceCameras');
      const data = response.data;
      console.log('Cameras fetched successfully:', data);
      setCameras(data);
    } catch (error) {
      console.error('Error fetching cameras:', error);
      setError('Failed to load cameras. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGraphData = async (date, device) => {
    try {
      const response = await fetch('http://localhost:8080/v2/evidenceVideo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, device })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const rawData = await response.json();

      if (!Array.isArray(rawData)) {
        console.error('API response is not an array:', rawData);
        throw new Error('Invalid API response: Expected an array');
      }

      // Process the data to match the expected format
      const formattedData = rawData.flatMap((bucket) => {
        if (!bucket.bucket || !bucket.label_wise) return [];

        const bucketTime = new Date(bucket.bucket.min).getTime();

        return bucket.label_wise.map((labelItem) => {
          return {
            x: bucketTime,
            y: labelItem.count,
            label: labelItem.label,
            trackIds: Array(labelItem.count).fill(0) // Create array of size count for trackIds
          };
        });
      });

      setGraphData(formattedData);
    } catch (error) {
      console.error('Error fetching graph data:', error);
    }
  };

  useEffect(() => {
    if (currentDate && selectedCamera.device_name) {
      fetchGraphData(currentDate, selectedCamera.device_name);
    }
  }, [currentDate, selectedCamera.device_name]);

  const colorMap = {
    Car: '#007BFF',
    EmployeePerson: '#4CAF50',
    GuardPerson: '#FF9800',
    Helmet: '#FF1744',
    HelmetPerson: '#9C27B0',
    Human: '#008080',
    ManualMhe: '#26A69A',
    OrangeEngineMhe: '#FF5722',
    OtherPerson: '#D32F2F',
    Palettes: '#6A1B9A',
    PpeKit: '#1976D2',
    PpePerson: '#388E3C',
    ProductBoxes: '#FBC02D',
    ShutterClose: '#E64A19',
    ShutterOpen: '#673AB7',
    Truck: '#5D4037',
    YellowEngineMhe: '#CDDC39',
    Default: '#9E9E9E'
  };

  const chartOptions = {
    chart: {
      type: 'area',
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: true }
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    markers: {
      size: 6,
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: { size: 8 }
    },
    xaxis: {
      type: 'datetime',
      title: {
        text: 'Timestamp',
        style: { fontSize: '12px', fontWeight: 500, color: '#000000' }
      },
      labels: { style: { colors: '#000000' } }
    },
    yaxis: {
      title: {
        text: 'Count',
        style: { fontSize: '12px', fontWeight: 500, color: '#000000' }
      },
      min: 0,
      labels: { style: { colors: '#000000' } }
    },
    tooltip: {
      shared: false,
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const data = w.config.series[seriesIndex].data[dataPointIndex];
        const time = new Date(data.x).toLocaleTimeString();

        return `
          <div style="
            background: white;
            color: #333;
            font-size: 14px;
            font-weight: 500;
            padding: 14px;
            border-radius: 12px;
            box-shadow: 0px 6px 15px rgba(0, 0, 0, 0.2);
            border: 1px solid #e0e4e7;
            text-align: left;
            min-width: 180px;
          ">
            <div style="
              border-bottom: 2px solid #4f46e5;
              padding-bottom: 8px;
              margin-bottom: 8px;
              font-size: 16px;
              font-weight: bold;
              color: #4f46e5;
            ">
              ${data.label}
            </div>
            <div style="margin-bottom: 4px;">
              <span style="color: #6b7280;">Time:</span> ${time}
            </div>
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 6px 0;
            ">
              <span style="color: #6b7280;">Count:</span>
              <span style="font-weight: bold;">${data.y}</span>
            </div>
          </div>`;
      }
    },
    grid: {
      borderColor: '#e0e0e0',
      row: { colors: ['transparent', 'transparent'], opacity: 0.5 }
    },
    theme: { mode: 'light' }
  };

  const capitalizeLabel = (label) => {
    if (!label) return 'Unknown';
    return label
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  };

  // Process graph data
  const groupedData = {};
  graphData.forEach((data) => {
    if (!data.label) return;

    const capitalizedLabel = capitalizeLabel(data.label);

    if (!groupedData[capitalizedLabel]) {
      groupedData[capitalizedLabel] = [];
    }
    groupedData[capitalizedLabel].push({
      x: data.x,
      y: data.y,
      label: capitalizedLabel,
      trackIds: data.trackIds
    });
  });

  const chartSeries = Object.keys(groupedData).map((label) => {
    const color = colorMap[label] || colorMap.Default;
    return {
      name: label,
      data: groupedData[label],
      color: color
    };
  });

  return (
    <div style={{ position: 'relative', top: '-45px', right: '20px', zIndex: 10 }}>
      <div style={{ display: 'flex', backgroundColor: theme.palette.background.default, height: '100vh' }}>
        <SideBar selField="videos" />

        <div
          style={{
            flexGrow: 1,
            marginLeft: '40px',
            width: isRightBarVisible ? '75%' : '100%',
            transition: 'width 0.3s ease',
            position: 'relative'
          }}
        >
          <div className="flex gap-2 w-full mb-3">
            <div className="w-full">
              <div ref={targetRef} className="w-full m-0 p-0">
                {isAnnotation ? (
                  <ImageAnnotationTool />
                ) : (
                  <EvidenceVideo key={selectedSource} vdoSource={source} videoList={videoData} setSource={setSource} ref={videoRef} />
                )}
              </div>
              <div className="flex mt-2 gap-1 mb-1 text-white">
                <div className="w-full h-[372px] rounded-lg p-3">
                  <div className="mt-4">
                    <ReactApexChart options={chartOptions} series={chartSeries} type="area" height={280} width="100%" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isEvidenceRightBarVisible && (
          <div
            className="ml-10 mr-0"
            style={{
              width: '25%',
              transition: 'all 0.3s ease',
              backgroundColor: theme.palette.background.paper,
              borderRadius: '12px',
              boxShadow: theme.shadows[1],
              border: `1px solid ${theme.palette.divider}`,
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 48px)'
            }}
          >
            <RightEvidenceBar videoSource={setSource} cameras={cameras} loading={loading} error={error} refreshCameras={fetchCameras} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidencePage;
